/**
 * Hook for sending a loan nudge (owner → borrower reminder).
 *
 * Calls the `send_loan_nudge` RPC, which is rate-limited to 1 nudge per
 * loan per 24 hours. Maps known error codes to user-friendly toast messages.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { captureSentryException } from '@/lib/sentry';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import posthog from '@/lib/posthog';

export function useLoanNudge() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ loanId, itemId }: { loanId: string; itemId: string }) => {
      const { error } = await supabase.rpc('send_loan_nudge', { p_loan_id: loanId });
      if (error) throw error;
      return { loanId, itemId };
    },
    onSuccess: (_data, variables) => {
      toast.success('Reminder sent');
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      posthog.capture('loan_nudge_sent', {
        loan_id: variables.loanId,
        item_id: variables.itemId,
      });
    },
    onError: (error: any) => {
      captureSentryException(error);
      const detail = error?.details || error?.message || '';
      if (detail.includes('NUDGE_RATE_LIMITED')) {
        toast.error('You can only send one reminder per day for this loan');
      } else if (detail.includes('NOT_ITEM_OWNER')) {
        toast.error('Only the item owner can send reminders');
      } else if (detail.includes('LOAN_NOT_ACTIVE')) {
        toast.error('This loan is no longer active');
      } else {
        toast.error('Failed to send reminder');
      }
    },
  });
}
